import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { LogIn, ArrowLeft, UserPlus, Terminal, AlertCircle } from "lucide-react";
import { consumeReturnPath } from "@/lib/authReturnPath";
import type { LocalAuthBypassRole } from "@shared/local-auth-bypass";

const SIGN_IN_INCOMPLETE = {
  title: "Sign-in didn't finish",
  description: "Something went wrong while completing your sign-in. Please try again.",
};

const LOGIN_ERRORS: Record<string, { title: string; description: string }> = {
  "access-denied": {
    title: "We couldn't match your account",
    description:
      "Your sign-in worked, but it isn't linked to an AIVIA account yet. Signing in again won't fix this — contact WorldVia support and we'll link it for you.",
  },
  "auth0-callback": SIGN_IN_INCOMPLETE,
  "missing-parameters": SIGN_IN_INCOMPLETE,
};

const readLoginError = () => {
  if (typeof window === "undefined") return null;
  const code = new URLSearchParams(window.location.search).get("error");
  return code ? LOGIN_ERRORS[code] ?? null : null;
};

const AUTO_REDIRECT_KEY = "aivia_auth0_auto_redirect_at";
const AUTO_REDIRECT_COOLDOWN_MS = 60_000;

// If a sign-in bounces straight back here, show the page instead of
// redirecting to Auth0 again in a loop.
const claimAutoRedirect = () => {
  try {
    const last = Number(sessionStorage.getItem(AUTO_REDIRECT_KEY) ?? 0);
    if (Date.now() - last < AUTO_REDIRECT_COOLDOWN_MS) return false;
    sessionStorage.setItem(AUTO_REDIRECT_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

const Login = () => {
  const {
    user,
    login,
    register,
    localLogin,
    localBypassEnabled,
    availableBypassRoles,
    isLoading,
  } = useAuth();
  const [pendingAction, setPendingAction] = useState<'login' | 'signup' | null>(null);
  const [localEmail, setLocalEmail] = useState('');
  const [localDisplayName, setLocalDisplayName] = useState('');
  const [localRole, setLocalRole] = useState<LocalAuthBypassRole>(
    availableBypassRoles[0] ?? 'ADVISOR',
  );
  const [localAgencyId, setLocalAgencyId] = useState('');
  const [localTeamId, setLocalTeamId] = useState('');
  const [localManagedTeamIds, setLocalManagedTeamIds] = useState('');
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);
  const [loginError] = useState(readLoginError);
  const [_, setLocation] = useLocation();
  const { toast } = useToast();
  const { data: loginOptions } = useQuery<{ autoRedirect: boolean }>({
    queryKey: ["/api/auth/login-options"],
    enabled: !localBypassEnabled,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!localBypassEnabled) {
      return;
    }
    if (
      availableBypassRoles.length > 0 &&
      !availableBypassRoles.includes(localRole)
    ) {
      setLocalRole(availableBypassRoles[0]);
    }
  }, [availableBypassRoles, localBypassEnabled, localRole]);

  const handleAuth = async (action: 'login' | 'signup') => {
    setPendingAction(action);
    try {
      if (action === 'login') {
        await login();
      } else {
        await register();
      }
    } catch (error: any) {
      toast({
        title: action === 'login' ? 'Auth0 login failed' : 'Auth0 signup failed',
        description:
          error?.message || 'We could not start the Auth0 flow. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPendingAction(null);
    }
  };

  useEffect(() => {
    if (
      !loginOptions?.autoRedirect ||
      localBypassEnabled ||
      loginError ||
      isLoading ||
      user ||
      pendingAction
    ) {
      return;
    }
    if (claimAutoRedirect()) {
      void handleAuth("login");
    }
    // handleAuth is recreated each render; the guards above decide when to run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginOptions?.autoRedirect, localBypassEnabled, loginError, isLoading, user, pendingAction]);

  const parseManagedTeamIds = (value: string): string[] =>
    value
      .split(/[\n,]/)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

  const handleLocalLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLocalSubmitting(true);
    try {
      const managedTeamIds = parseManagedTeamIds(localManagedTeamIds);

      await localLogin({
        email: localEmail,
        role: localRole,
        displayName: localDisplayName || undefined,
        ...(localAgencyId.trim().length
          ? { agencyId: localAgencyId.trim() }
          : {}),
        ...(localTeamId.trim().length
          ? { teamId: parseInt(localTeamId.trim(), 10) || undefined }
          : {}),
        ...(managedTeamIds.length ? { managedTeamIds: managedTeamIds.map(Number).filter(Number.isFinite) } : {}),
      });
      toast({
        title: 'Signed in locally',
        description: `${localEmail} · ${localRole}`,
      });
      setLocalDisplayName('');
      setLocalEmail('');
      setLocalAgencyId('');
      setLocalTeamId('');
      setLocalManagedTeamIds('');
      setLocation(consumeReturnPath());
    } catch (error: any) {
      toast({
        title: 'Local login failed',
        description:
          error?.message ?? 'Unable to establish a local development session.',
        variant: 'destructive',
      });
    } finally {
      setIsLocalSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => setLocation(consumeReturnPath())}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <Card className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50">
                <CardHeader className="text-center pb-6">
                  <div className="flex justify-center mb-6">
                    <h1 className="text-3xl font-bold text-transparent bg-gradient-to-r from-orange-300 to-emerald-300 bg-clip-text">
                      AIVIA
                    </h1>
                  </div>
                  <CardTitle className="text-2xl font-bold text-white">
                    Welcome back
                  </CardTitle>
                  <CardDescription className="text-gray-300">
                    Sign in with Auth0 to continue
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-8 pb-8 space-y-6">
                  {loginError && (
                    <Alert
                      data-testid="login-error"
                      className="border-red-500/50 bg-red-950/30 text-red-100 [&>svg]:text-red-300"
                    >
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>{loginError.title}</AlertTitle>
                      <AlertDescription className="text-red-100/80">
                        {loginError.description}
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-6">
                    <Button
                      type="button"
                      data-testid="auth0-login-button"
                      className="w-full bg-gradient-to-r from-orange-500 to-emerald-500 hover:from-orange-600 hover:to-emerald-600 text-white font-bold py-4 text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-orange-500/25 rounded-2xl"
                      disabled={isLoading || pendingAction !== null || isLocalSubmitting}
                      onClick={() => handleAuth('login')}
                    >
                      {pendingAction === 'login' ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Redirecting to Auth0...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <LogIn className="h-5 w-5" />
                          Sign In with Auth0
                        </span>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-slate-600 text-white hover:border-orange-500"
                      disabled={isLoading || pendingAction !== null || isLocalSubmitting}
                      onClick={() => handleAuth('signup')}
                    >
                      {pendingAction === 'signup' ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Redirecting to Auth0...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <UserPlus className="h-5 w-5" />
                          Create Account
                        </span>
                      )}
                    </Button>
                  </div>
                  {localBypassEnabled && (
                    <>
                      <Separator className="bg-slate-700/60" />
                      <div className="space-y-4 rounded-2xl border border-amber-400/40 bg-amber-950/20 p-5">
                        <div className="flex items-center gap-2 text-amber-200">
                          <Terminal className="h-4 w-4" />
                          <span className="text-sm font-semibold uppercase tracking-wide">
                            Local development bypass
                          </span>
                        </div>
                        <p className="text-sm text-amber-100/80">
                          Sign in without Auth0 for local iteration. Never enable this flow in staging or production environments.
                        </p>
                        <form className="space-y-4" onSubmit={handleLocalLogin}>
                          <div className="space-y-2">
                            <Label htmlFor="local-email" className="text-amber-100">
                              Email
                            </Label>
                            <Input
                              id="local-email"
                              data-testid="local-email"
                              type="email"
                              value={localEmail}
                              onChange={(event) => setLocalEmail(event.target.value)}
                              required
                              autoComplete="email"
                              placeholder="advisor@example.com"
                              className="border-amber-400/40 bg-slate-950/40 text-white placeholder:text-slate-400"
                              disabled={isLocalSubmitting || isLoading}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="local-role" className="text-amber-100">
                              Role
                            </Label>
                            <Select
                              value={localRole}
                              onValueChange={(value) =>
                                setLocalRole(value as LocalAuthBypassRole)
                              }
                              disabled={isLocalSubmitting || isLoading}
                            >
                              <SelectTrigger data-testid="local-role-select" className="border-amber-400/40 bg-slate-950/40 text-white">
                                <SelectValue placeholder="Select a role" />
                              </SelectTrigger>
                              <SelectContent className="border-amber-400/40 bg-slate-950/95 text-white">
                                {availableBypassRoles.map((role) => (
                                  <SelectItem key={role} value={role} data-testid={`role-option-${role}`}>
                                    {role.replace(/_/g, ' ')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="local-name" className="text-amber-100">
                              Display name <span className="text-amber-300/70">(optional)</span>
                            </Label>
                            <Input
                              id="local-name"
                              data-testid="local-display-name"
                              value={localDisplayName}
                              onChange={(event) => setLocalDisplayName(event.target.value)}
                              placeholder="Jane Advisor"
                              className="border-amber-400/40 bg-slate-950/40 text-white placeholder:text-slate-400"
                              disabled={isLocalSubmitting || isLoading}
                            />
                          </div>
                          <details className="rounded-xl border border-amber-400/40 bg-slate-950/30 px-4 py-3 text-sm text-amber-100/90">
                            <summary className="cursor-pointer select-none font-medium text-amber-200 hover:text-amber-100">
                              Scope (optional)
                            </summary>
                            <div className="mt-4 space-y-4">
                              <div className="space-y-1">
                                <Label htmlFor="local-agency" className="text-amber-100">
                                  Agency ID
                                </Label>
                                <Input
                                  id="local-agency"
                                  data-testid="local-agency-id"
                                  value={localAgencyId}
                                  onChange={(event) => setLocalAgencyId(event.target.value)}
                                  placeholder="agency_123"
                                  autoComplete="off"
                                  className="border-amber-400/40 bg-slate-950/40 text-white placeholder:text-slate-500"
                                  disabled={isLocalSubmitting || isLoading}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor="local-team" className="text-amber-100">
                                  Team ID
                                </Label>
                                <Input
                                  id="local-team"
                                  data-testid="local-team-id"
                                  value={localTeamId}
                                  onChange={(event) => setLocalTeamId(event.target.value)}
                                  placeholder="team_456"
                                  autoComplete="off"
                                  className="border-amber-400/40 bg-slate-950/40 text-white placeholder:text-slate-500"
                                  disabled={isLocalSubmitting || isLoading}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor="local-managed-teams" className="text-amber-100">
                                  Managed Team IDs
                                </Label>
                                <Textarea
                                  id="local-managed-teams"
                                  value={localManagedTeamIds}
                                  onChange={(event) => setLocalManagedTeamIds(event.target.value)}
                                  placeholder="Comma or newline separated"
                                  className="border-amber-400/40 bg-slate-950/40 text-white placeholder:text-slate-500"
                                  rows={3}
                                  disabled={isLocalSubmitting || isLoading}
                                />
                              </div>
                            </div>
                          </details>
                          <Button
                            type="submit"
                            data-testid="local-login-submit"
                            className="w-full bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/60 text-amber-100 font-semibold"
                            disabled={
                              isLocalSubmitting ||
                              isLoading ||
                              localEmail.trim().length === 0
                            }
                          >
                            {isLocalSubmitting ? (
                              <span className="flex items-center justify-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-amber-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Establishing session...
                              </span>
                            ) : (
                              <span className="flex items-center justify-center gap-2">
                                <Terminal className="h-4 w-4" />
                                Sign In Locally
                              </span>
                            )}
                          </Button>
                        </form>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="order-1 lg:order-2">
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                    Your intelligent workspace for travel professionals
                  </h2>
                  <p className="text-xl text-gray-300">
                    Connect with AI-powered travel recommendations and streamline your workflow with advanced tools designed for the modern travel advisor.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
